/**
 * localStorage persistence layer for Frostgrave Helper.
 *
 * Keys:
 *   frostgrave:schema-version   → number
 *   frostgrave:warbands         → WarbandIndexEntry[]  (list of all warbands, metadata only)
 *   frostgrave:warband:{id}     → StoredWarbandData    (full warband state)
 *   frostgrave:cookie-consent   → 'accepted' | 'declined'
 */

import {
  Warband,
  SpellcasterDTO,
  SoldierDTO,
  CaptainDTO,
  CaptainAdvance,
  VaultItemDTO,
  BaseResource,
  BuffOrDebuff,
  Spell,
  WarbandLevelUp,
  Weapon,
  Armour,
  TreasureItem,
  WordEffect,
  PermanentInjury,
  GameSystem,
  StargraveCrewData,
} from '../types';
import { applySchoolAndLevelUps, applyBaseResourceCastingBonuses, categoriseSpellsBySchool } from '../utils/spellCastingNumbers';

// --------------------------------------------------------------------------
// Stored (compact) types — IDs only, no hydrated objects
// --------------------------------------------------------------------------

export interface StoredMember {
  id: string;
  creatureId: string;
  name: string;
  currentHealth: number;
  notes: string | null;
  isTemporary: boolean;
  weaponIds: string[];
  armourIds: string[];
  buffIds: string[];
  treasureItemIds: string[];
  storedInInn: number;
  leftTable: boolean;
  carryingTreasure: boolean;
  poisoned?: boolean;
  permanentInjuryIds?: string[];
  /** Drive file ID for this member's portrait — absent when no image has been uploaded via Drive. */
  driveImageId?: string;
}

export interface StoredSoldier extends StoredMember {
  type: string;
  cost: number;
  costPaid?: number | null;
}

export interface StoredCaptain extends StoredMember {
  experience: number;
  trickIds: string[];
  advances: CaptainAdvance[];
  costPaid?: number | null;
}

export interface StoredVaultItem {
  instanceId: string;
  itemId: string;
  parentItemId: string | null;
  warbandMemberId: string | null;
  costPaid?: number | null;
}

export interface StoredWarbandData {
  schemaVersion: number;
  warband: Warband;
  wizard: StoredMember | null;
  apprentice: StoredMember | null;
  captain?: StoredCaptain | null;
  soldiers: StoredSoldier[];
  selectedSpellIds: string[];
  levelUps: WarbandLevelUp[];
  vaultItems: StoredVaultItem[];
  selectedBaseResourceIds: string[];
  baseResourceCostsPaid?: Record<string, number>;
  warbandBuffIds: string[];
  wordEffects?: WordEffect[];
  /** Remaining uses per item per member. Key: "${memberId}:${itemId}". Absence means full uses remaining. */
  itemUses?: Record<string, number>;
  stargrave?: StargraveCrewData;
}

export interface PendingMemberImage {
  warbandId: string;
  memberId: string;
  source: 'local-upload' | 'drive-selection';
  baseDriveImageId: string | null;
  selectedDriveImageId?: string;
}

// --------------------------------------------------------------------------
// localStorage keys
// --------------------------------------------------------------------------

const KEY_SCHEMA_VERSION = 'frostgrave:schema-version';
const KEY_WARBANDS = 'frostgrave:warbands';
const warbandKey = (id: string) => `frostgrave:warband:${id}`;
const KEY_COOKIE_CONSENT = 'frostgrave:cookie-consent';
const KEY_GOOGLE_IDENTITY = 'frostgrave:google-identity';
const KEY_DEVICE_ID = 'frostgrave:sync-device-id';
const PENDING_MEMBER_IMAGE_PREFIX = 'frostgrave:pending-member-image:';
const syncOutboxKey = (accountId: string) => `frostgrave:sync:${accountId}:outbox`;
const syncMetadataKey = (accountId: string) => `frostgrave:sync:${accountId}:metadata`;

const CURRENT_SCHEMA_VERSION = 14;

const normalizeStargraveGameState = (
  gameState: Partial<StargraveCrewData['soldiers'][number]['gameState']> | undefined,
  maximumHealth: number
) => ({
  currentHealth: gameState?.currentHealth ?? maximumHealth,
  stunned: gameState?.stunned ?? false,
  poisoned: gameState?.poisoned ?? false,
  loot: gameState?.loot ?? null,
  leftTable: gameState?.leftTable ?? false,
  activeBuffs: gameState?.activeBuffs ?? [],
});

const normalizeGameSystem = (value: unknown): GameSystem =>
  value === 'stargrave' ? 'stargrave' : 'frostgrave';

const normalizeWarband = (warband: Warband): Warband => ({
  ...warband,
  gameSystem: normalizeGameSystem(warband.gameSystem),
});

const normalizeStoredWarband = (stored: StoredWarbandData): StoredWarbandData => {
  const gameSystem = normalizeGameSystem(stored.warband.gameSystem);
  const isLegacyStargraveCrew = gameSystem === 'stargrave' && stored.schemaVersion < 4;
  const warband = normalizeWarband(stored.warband);

  return {
    ...stored,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    warband: isLegacyStargraveCrew && warband.gold === 0 ? { ...warband, gold: 400 } : warband,
    ...(gameSystem === 'stargrave'
      ? {
          stargrave: {
            captain: stored.stargrave?.captain
              ? { ...stored.stargrave.captain, permanentInjuryIds: stored.stargrave.captain.permanentInjuryIds ?? [], gameState: normalizeStargraveGameState(stored.stargrave.captain.gameState, stored.stargrave.captain.stats.health) }
              : null,
            firstMate: stored.stargrave?.firstMate
              ? { ...stored.stargrave.firstMate, permanentInjuryIds: stored.stargrave.firstMate.permanentInjuryIds ?? [], gameState: normalizeStargraveGameState(stored.stargrave.firstMate.gameState, stored.stargrave.firstMate.stats.health) }
              : null,
            soldiers: (stored.stargrave?.soldiers ?? []).map((soldier) => ({
              ...soldier,
              gameState: normalizeStargraveGameState(
                soldier.gameState ?? { currentHealth: (soldier as unknown as { currentHealth?: number }).currentHealth },
                0
              ),
            })),
            advancedTechnology: stored.stargrave?.advancedTechnology ?? [],
            advancedWeapons: stored.stargrave?.advancedWeapons ?? [],
            alienArtefacts: stored.stargrave?.alienArtefacts ?? [],
            secrets: stored.stargrave?.secrets ?? 0,
            ship: stored.stargrave?.ship ?? { name: 'Unnamed Ship', upgradeIds: [] },
          },
        }
      : {}),
  };
};

const pendingMemberImageKey = (warbandId: string, memberId: string) =>
  `${PENDING_MEMBER_IMAGE_PREFIX}${warbandId}:${memberId}`;

// --------------------------------------------------------------------------
// Internal helpers
// --------------------------------------------------------------------------

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
    console.warn('localStorage write failed for key:', key);
  }
}

// --------------------------------------------------------------------------
// Warband index (list of all warbands, metadata only)
// --------------------------------------------------------------------------

export function getAllWarbands(): Warband[] {
  return (read<Warband[]>(KEY_WARBANDS) ?? []).map(normalizeWarband);
}

export function saveWarbandIndex(warbands: Warband[]): void {
  write(KEY_WARBANDS, warbands.map(normalizeWarband));
}

// --------------------------------------------------------------------------
// Full warband data
// --------------------------------------------------------------------------

export function loadWarbandData(id: string): StoredWarbandData | null {
  const stored = read<StoredWarbandData>(warbandKey(id));
  if (!stored) return null;
  const normalized = normalizeStoredWarband(stored);
  if (stored.schemaVersion !== CURRENT_SCHEMA_VERSION) {
    write(warbandKey(id), normalized);
    const warbands = getAllWarbands();
    saveWarbandIndex(warbands.map((warband) => warband.id === id ? normalized.warband : warband));
  }
  return normalized;
}

export function saveWarbandData(id: string, data: StoredWarbandData): void {
  const normalized = normalizeStoredWarband(data);
  write(warbandKey(id), normalized);
  // Keep the index in sync
  const warbands = getAllWarbands();
  const exists = warbands.some((w) => w.id === id);
  if (!exists) {
    saveWarbandIndex([...warbands, normalized.warband]);
  } else {
    saveWarbandIndex(warbands.map((w) => (w.id === id ? normalized.warband : w)));
  }
}

export function deleteWarband(id: string): void {
  localStorage.removeItem(warbandKey(id));
  saveWarbandIndex(getAllWarbands().filter((w) => w.id !== id));
}

export function removeBuffReferences(buffId: string): void {
  for (const warband of getAllWarbands()) {
    const stored = loadWarbandData(warband.id);
    if (!stored) continue;

    const removeFromMember = <T extends StoredMember | null>(member: T): T => {
      if (!member) return member;
      return { ...member, buffIds: member.buffIds.filter((id) => id !== buffId) } as T;
    };

    saveWarbandData(warband.id, {
      ...stored,
      wizard: removeFromMember(stored.wizard),
      apprentice: removeFromMember(stored.apprentice),
      captain: removeFromMember(stored.captain ?? null),
      soldiers: stored.soldiers.map((soldier) => removeFromMember(soldier)),
      warbandBuffIds: stored.warbandBuffIds.filter((id) => id !== buffId),
    });
  }
}

// --------------------------------------------------------------------------
// Create a new warband (generates UUID, writes to localStorage)
// --------------------------------------------------------------------------

export function createWarband(data: {
  name: string;
  description: string;
  schoolId?: string;
  baseId?: string | null;
  gameSystem?: GameSystem;
}): StoredWarbandData {
  const gameSystem = data.gameSystem ?? 'frostgrave';
  const warband: Warband = {
    id: crypto.randomUUID(),
    gameSystem,
    name: data.name,
    description: data.description,
    schoolId: data.schoolId ?? '',
    baseId: data.baseId ?? null,
    gold: 400,
    experience: 0,
    apprenticePromotions: 0,
  };

  const stored: StoredWarbandData = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    warband,
    wizard: null,
    apprentice: null,
    captain: null,
    soldiers: [],
    selectedSpellIds: [],
    levelUps: [],
    vaultItems: [],
    selectedBaseResourceIds: [],
    baseResourceCostsPaid: {},
    warbandBuffIds: [],
    wordEffects: [],
    itemUses: {},
    ...(gameSystem === 'stargrave'
      ? { stargrave: { captain: null, firstMate: null, soldiers: [], advancedTechnology: [], advancedWeapons: [], alienArtefacts: [], secrets: 0, ship: { name: 'Unnamed Ship', upgradeIds: [] } } }
      : {}),
  };

  saveWarbandData(warband.id, stored);
  return stored;
}

// --------------------------------------------------------------------------
// Hydration (StoredXxx → full DTO)
// --------------------------------------------------------------------------

function lookupWeapons(ids: string[], weaponMap: Map<string, Weapon>): Weapon[] {
  return ids.map(
    (id) =>
      weaponMap.get(id) ?? {
        id,
        name: id,
        damageModifier: null,
        maxRange: null,
        encumbering: null,
        slotsConsumed: null,
        notes: null,
        description: null,
        specialTags: [],
      }
  );
}

function lookupArmours(ids: string[], armourMap: Map<string, Armour>): Armour[] {
  return ids.map(
    (id) =>
      armourMap.get(id) ?? {
        id,
        name: id,
        armourModifier: null,
        encumbering: null,
        notes: null,
        description: null,
        specialTags: [],
      }
  );
}

function lookupBuffs(ids: string[], buffMap: Map<string, BuffOrDebuff>): BuffOrDebuff[] {
  const countById = new Map<string, number>();
  for (const id of ids) countById.set(id, (countById.get(id) ?? 0) + 1);
  return Array.from(countById.entries())
    .map(([id, count]) => {
      const buff = buffMap.get(id);
      return buff ? ({ ...buff, stackCount: count } as BuffOrDebuff) : null;
    })
    .filter((b): b is BuffOrDebuff => b !== null);
}

function lookupTreasureItems(ids: string[], treasureMap: Map<string, TreasureItem>): TreasureItem[] {
  return ids
    .map((id) => treasureMap.get(id))
    .filter((t): t is TreasureItem => t !== null && t !== undefined);
}

export interface HydrationMaps {
  weaponMap: Map<string, Weapon>;
  armourMap: Map<string, Armour>;
  buffMap: Map<string, BuffOrDebuff>;
  treasureMap: Map<string, TreasureItem>;
  baseResourceMap: Map<string, BaseResource>;
  injuryMap: Map<string, PermanentInjury>;
}

export function buildHydrationMaps(refData: {
  weapons: Weapon[];
  armours: Armour[];
  buffs: BuffOrDebuff[];
  treasureItems: TreasureItem[];
  baseResources: BaseResource[];
  injuries?: PermanentInjury[];
}): HydrationMaps {
  return {
    weaponMap: new Map(refData.weapons.map((w) => [w.id, w])),
    armourMap: new Map(refData.armours.map((a) => [a.id, a])),
    buffMap: new Map(refData.buffs.map((b) => [b.id, b])),
    treasureMap: new Map(refData.treasureItems.map((t) => [t.id, t])),
    baseResourceMap: new Map(refData.baseResources.map((r) => [r.id, r])),
    injuryMap: new Map((refData.injuries ?? []).map((i) => [i.id, i])),
  };
}

function hydrateSpellcaster(
  stored: StoredMember,
  warbandId: string,
  maps: HydrationMaps
): SpellcasterDTO {
  return {
    id: stored.id,
    creatureId: stored.creatureId,
    warbandId,
    name: stored.name,
    currentHealth: stored.currentHealth,
    notes: stored.notes,
    isTemporary: stored.isTemporary,
    // These stats come from the creature — we don't re-derive them here; the
    // stored member was originally created from a Creature row in the reference
    // data and the stats were captured at creation time (or through level-ups).
    // We persist the full stats on the StoredMember so we can restore them.
    move: (stored as unknown as Record<string, number>).move ?? 6,
    fight: (stored as unknown as Record<string, number>).fight ?? 2,
    shoot: (stored as unknown as Record<string, number>).shoot ?? 0,
    armour: (stored as unknown as Record<string, number>).armour ?? 10,
    will: (stored as unknown as Record<string, number>).will ?? 4,
    health: (stored as unknown as Record<string, number>).health ?? 14,
    itemSlots: (stored as unknown as Record<string, number>).itemSlots ?? 5,
    creatureName: (stored as unknown as Record<string, string>).creatureName ?? stored.name,
    leftTable: stored.leftTable,
    carryingTreasure: stored.carryingTreasure,
    poisoned: stored.poisoned ?? false,
    storedInInn: stored.storedInInn,
    buffsAndDebuffs: lookupBuffs(stored.buffIds, maps.buffMap),
    weapons: lookupWeapons(stored.weaponIds, maps.weaponMap),
    armours: lookupArmours(stored.armourIds, maps.armourMap),
    treasureItems: lookupTreasureItems(stored.treasureItemIds, maps.treasureMap),
    permanentInjuries: (stored.permanentInjuryIds ?? []).map((id) => maps.injuryMap.get(id)).filter((i): i is NonNullable<typeof i> => i !== undefined),
  };
}

function hydrateSoldier(
  stored: StoredSoldier,
  warbandId: string,
  maps: HydrationMaps
): SoldierDTO {
  const base = hydrateSpellcaster(stored, warbandId, maps);
  return {
    ...base,
    armours: base.armours ?? [],
    type: stored.type,
    cost: stored.cost,
    costPaid: stored.costPaid,
  };
}

function hydrateCaptain(
  stored: StoredCaptain,
  warbandId: string,
  maps: HydrationMaps
): CaptainDTO {
  const base = hydrateSpellcaster(stored, warbandId, maps);
  return {
    ...base,
    armours: base.armours ?? [],
    experience: stored.experience ?? 0,
    trickIds: stored.trickIds ?? [],
    advances: stored.advances ?? [],
    costPaid: stored.costPaid,
  };
}

function hydrateVaultItem(stored: StoredVaultItem, warbandId: string, maps: HydrationMaps): VaultItemDTO {
  const item = maps.treasureMap.get(stored.itemId);
  return {
    id: stored.itemId,
    name: item?.name ?? stored.itemId,
    description: item?.description ?? null,
    refreshAfterGame: item?.refreshAfterGame ?? false,
    usesPerGame: item?.usesPerGame ?? 0,
    purchasePrice: item?.purchasePrice ?? null,
    salePrice: item?.salePrice ?? 0,
    ingredientsPrice: item?.ingredientsPrice ?? null,
    invokedSpellId: item?.invokedSpellId ?? null,
    buffId: item?.buffId ?? null,
    baseItemId: item?.baseItemId ?? null,
    baseItemType: item?.baseItemType ?? null,
    instanceId: stored.instanceId,
    warbandId,
    parentItemId: stored.parentItemId,
    warbandMemberId: stored.warbandMemberId,
    costPaid: stored.costPaid,
  };
}

// --------------------------------------------------------------------------
// Full hydration: StoredWarbandData → all state needed by warbandStore
// --------------------------------------------------------------------------

export interface HydratedWarbandState {
  warband: Warband;
  wizard: SpellcasterDTO | null;
  apprentice: SpellcasterDTO | null;
  captain: CaptainDTO | null;
  soldiers: SoldierDTO[];
  vaultItems: VaultItemDTO[];
  selectedBaseResources: BaseResource[];
  baseResourceCostsPaid: Record<string, number>;
  warbandBuffs: BuffOrDebuff[];
  wordEffects: WordEffect[];
  selectedSpells: Spell[];        // spells with adjusted castingNumber
  allAvailableSpells: Spell[];    // all spells adjusted for warband's school
  primarySpells: Spell[];
  alignedSpells: Spell[][];
  neutralSpells: Spell[][];
  levelUps: WarbandLevelUp[];
  availableBuffs: BuffOrDebuff[]; // all buffs (used by buff picker UI)
}

export function hydrateWarbandState(
  stored: StoredWarbandData,
  allSpells: Spell[],
  allBuffs: BuffOrDebuff[],
  maps: HydrationMaps
): HydratedWarbandState {
  const { warband } = stored;

  const wizard = stored.wizard
    ? hydrateSpellcaster(stored.wizard, warband.id, maps)
    : null;
  const apprentice = stored.apprentice
    ? hydrateSpellcaster(stored.apprentice, warband.id, maps)
    : null;
  const captain = stored.captain
    ? hydrateCaptain(stored.captain, warband.id, maps)
    : null;
  const soldiers = stored.soldiers.map((s) => hydrateSoldier(s, warband.id, maps));
  const vaultItems = stored.vaultItems.map((v) => hydrateVaultItem(v, warband.id, maps));

  // Reconcile warbandMemberId on vault items for saves that predate this tracking.
  // For each member's treasureItemIds, find the first matching vault instance that
  // has no warbandMemberId set and assign it. This is idempotent: instances that
  // already have warbandMemberId set are skipped.
  const allMembersForReconciliation = [
    ...(wizard ? [{ id: wizard.id, treasureItems: wizard.treasureItems }] : []),
    ...(apprentice ? [{ id: apprentice.id, treasureItems: apprentice.treasureItems }] : []),
    ...(captain ? [{ id: captain.id, treasureItems: captain.treasureItems }] : []),
    ...soldiers.map((s) => ({ id: s.id, treasureItems: s.treasureItems })),
  ];
  for (const member of allMembersForReconciliation) {
    const typeCursor = new Map<string, number>();
    for (const item of member.treasureItems ?? []) {
      const cursor = typeCursor.get(item.id) ?? 0;
      // Find the (cursor)th unassigned vault instance of this type
      let seen = 0;
      for (const vaultItem of vaultItems) {
        if (vaultItem.id !== item.id || vaultItem.warbandMemberId !== null) continue;
        if (seen === cursor) {
          vaultItem.warbandMemberId = member.id;
          break;
        }
        seen++;
      }
      typeCursor.set(item.id, cursor + 1);
    }
  }

  const selectedBaseResources = stored.selectedBaseResourceIds
    .map((id) => maps.baseResourceMap.get(id))
    .filter((r): r is BaseResource => r !== null && r !== undefined);
  const warbandBuffs = lookupBuffs(stored.warbandBuffIds, maps.buffMap);

  // Build spell improvements map from levelUps
  const spellImprovements: Record<string, number> = {};
  for (const lu of stored.levelUps) {
    if (lu.spellImprovement) {
      spellImprovements[lu.spellImprovement] = (spellImprovements[lu.spellImprovement] ?? 0) + 1;
    }
  }

  // All spells adjusted for warband school and base resource bonuses
  const allAdjusted = applyBaseResourceCastingBonuses(
    applySchoolAndLevelUps(allSpells, warband.schoolId, {}),
    stored.selectedBaseResourceIds
  );
  const allAvailableSpells = [...allAdjusted].sort((a, b) => a.name.localeCompare(b.name));

  // Selected spells with level-up improvements and base resource bonuses applied
  const selectedSpellSet = new Set(stored.selectedSpellIds);
  const selectedSpells = applyBaseResourceCastingBonuses(
    applySchoolAndLevelUps(
      allSpells.filter((s) => selectedSpellSet.has(s.id)),
      warband.schoolId,
      spellImprovements
    ),
    stored.selectedBaseResourceIds
  );

  const { primarySpells, alignedSpells, neutralSpells } = categoriseSpellsBySchool(
    allAdjusted,
    warband.schoolId
  );

  return {
    warband,
    wizard,
    apprentice,
    captain,
    soldiers,
    vaultItems,
    selectedBaseResources,
    baseResourceCostsPaid: stored.baseResourceCostsPaid ?? {},
    warbandBuffs,
    wordEffects: stored.wordEffects ?? [],
    selectedSpells,
    allAvailableSpells,
    primarySpells,
    alignedSpells,
    neutralSpells,
    levelUps: stored.levelUps,
    availableBuffs: allBuffs,
  };
}

// --------------------------------------------------------------------------
// Dehydration (DTO → StoredXxx) — exported for use in warbandStore subscriber
// --------------------------------------------------------------------------

export function dehydrateMemberForStorage(
  dto: SpellcasterDTO,
  driveImageId?: string
): StoredMember {
  return {
    id: dto.id,
    creatureId: dto.creatureId,
    name: dto.name,
    currentHealth: dto.currentHealth,
    notes: dto.notes,
    isTemporary: dto.isTemporary,
    weaponIds: dto.weapons.map((w) => w.id),
    armourIds: dto.armours?.map((a) => a.id) ?? [],
    buffIds: (dto.buffsAndDebuffs ?? []).flatMap((b) =>
      Array(b.stackCount ?? 1).fill(b.id)
    ),
    treasureItemIds: dto.treasureItems.map((t) => t.id),
    storedInInn: dto.storedInInn,
    leftTable: dto.leftTable,
    carryingTreasure: dto.carryingTreasure,
    poisoned: dto.poisoned ?? false,
    permanentInjuryIds: (dto.permanentInjuries ?? []).map((i) => i.id),
    ...(driveImageId ? { driveImageId } : {}),
    // Persist stats so they can be restored without reference data on load
    move: dto.move,
    fight: dto.fight,
    shoot: dto.shoot,
    armour: dto.armour,
    will: dto.will,
    health: dto.health,
    itemSlots: dto.itemSlots,
    creatureName: dto.creatureName,
  } as unknown as StoredMember;
}

export function dehydrateSoldierForStorage(
  dto: SoldierDTO,
  driveImageId?: string
): StoredSoldier {
  return {
    ...dehydrateMemberForStorage(dto, driveImageId),
    type: dto.type,
    cost: dto.cost,
    costPaid: dto.costPaid,
  };
}

export function dehydrateCaptainForStorage(
  dto: CaptainDTO,
  driveImageId?: string
): StoredCaptain {
  return {
    ...dehydrateMemberForStorage(dto, driveImageId),
    experience: dto.experience,
    trickIds: dto.trickIds,
    advances: dto.advances,
    costPaid: dto.costPaid,
  };
}

export function dehydrateVaultItemForStorage(dto: VaultItemDTO): StoredVaultItem {
  return {
    instanceId: dto.instanceId ?? crypto.randomUUID(),
    itemId: dto.id,
    parentItemId: dto.parentItemId,
    warbandMemberId: dto.warbandMemberId,
    costPaid: dto.costPaid,
  };
}

// --------------------------------------------------------------------------
// Persist current warbandStore state to localStorage
// --------------------------------------------------------------------------

/**
 * Reads the current state from warbandStore (lazily to avoid circular imports)
 * and persists it to localStorage.
 */
export function persistCurrentState(warbandId: string): void {
  // Lazy import to avoid circular dependency with the store
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useWarbandStore } = require('../stores/warbandStore') as typeof import('../stores/warbandStore');
  const state = useWarbandStore.getState();

  const existing = loadWarbandData(warbandId);
  if (!existing) return; // warband doesn't exist yet — nothing to persist

  const currentWarband = state.warbands.find((w) => w.id === warbandId) ?? existing.warband;
  const existingImageIds = new Map(
    [existing.wizard, existing.apprentice, existing.captain ?? null, ...existing.soldiers]
      .filter((member): member is StoredMember => member !== null)
      .map((member) => [member.id, member.driveImageId] as const)
  );

  const updated: StoredWarbandData = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    warband: currentWarband,
    wizard: state.wizardData
      ? dehydrateMemberForStorage(state.wizardData, existingImageIds.get(state.wizardData.id))
      : null,
    apprentice: state.apprenticeData
      ? dehydrateMemberForStorage(state.apprenticeData, existingImageIds.get(state.apprenticeData.id))
      : null,
    captain: state.captainData
      ? dehydrateCaptainForStorage(state.captainData, existingImageIds.get(state.captainData.id))
      : null,
    soldiers: (state.soldiersData?.soldiers ?? []).map((soldier) =>
      dehydrateSoldierForStorage(soldier, existingImageIds.get(soldier.id))
    ),
    selectedSpellIds: (state.spellcastersAndSpells?.warbandSpells ?? []).map((s) => s.id),
    levelUps: existing.levelUps, // levelUps are managed separately via saveLevelUps
    vaultItems: state.vaultItems.map(dehydrateVaultItemForStorage),
    selectedBaseResourceIds: state.baseResources.map((r) => r.id),
    baseResourceCostsPaid: state.baseResourceCostsPaid,
    warbandBuffIds: (state.warbandBuffs ?? []).flatMap((b) =>
      Array(b.stackCount ?? 1).fill(b.id)
    ),
    wordEffects: state.wordEffects ?? [],
    stargrave: existing.stargrave,
  };

  saveWarbandData(warbandId, updated);
}

/**
 * Persists level-ups for a warband (called after saveLevelUps).
 */
export function saveLevelUps(warbandId: string, levelUps: WarbandLevelUp[], experience: number): void {
  const existing = loadWarbandData(warbandId);
  if (!existing) return;
  const updated: StoredWarbandData = {
    ...existing,
    levelUps,
    warband: { ...existing.warband, experience },
  };
  saveWarbandData(warbandId, updated);
}

/**
 * Schema version check — can be used for future migrations.
 */
export function getSchemaVersion(): number {
  return read<number>(KEY_SCHEMA_VERSION) ?? 0;
}

export function writeSchemaVersion(): void {
  write(KEY_SCHEMA_VERSION, CURRENT_SCHEMA_VERSION);
}

// --------------------------------------------------------------------------
// Item usage tracking
// --------------------------------------------------------------------------

const itemUsageKey = (memberId: string, itemId: string) => `${memberId}:${itemId}`;

/** Returns the remaining uses for an item on a member. Falls back to the item's full usesPerGame. */
export function getItemRemainingUses(
  warbandId: string,
  memberId: string,
  item: { id: string; usesPerGame: number }
): number {
  const data = loadWarbandData(warbandId);
  const key = itemUsageKey(memberId, item.id);
  const stored = data?.itemUses?.[key];
  return stored !== undefined ? stored : item.usesPerGame;
}

/** Persists the remaining uses for an item on a member. */
export function setItemRemainingUses(
  warbandId: string,
  memberId: string,
  itemId: string,
  count: number
): void {
  const data = loadWarbandData(warbandId);
  if (!data) return;
  data.itemUses = { ...(data.itemUses ?? {}), [itemUsageKey(memberId, itemId)]: count };
  saveWarbandData(warbandId, data);
}

/** Removes the usage tracking entry for a specific item (call when item is removed from member). */
export function clearItemRemainingUses(
  warbandId: string,
  memberId: string,
  itemId: string
): void {
  const data = loadWarbandData(warbandId);
  if (!data?.itemUses) return;
  const key = itemUsageKey(memberId, itemId);
  const updated = { ...data.itemUses };
  delete updated[key];
  data.itemUses = updated;
  saveWarbandData(warbandId, data);
}

export function getCookieConsent(): 'accepted' | 'declined' | null {
  const value = localStorage.getItem(KEY_COOKIE_CONSENT);
  if (value === 'accepted' || value === 'declined') return value;
  return null;
}

export function setCookieConsent(choice: 'accepted' | 'declined'): void {
  localStorage.setItem(KEY_COOKIE_CONSENT, choice);
}

export interface RememberedGoogleIdentity {
  sub: string;
  name: string;
  email: string;
  picture: string;
}

export function getRememberedGoogleIdentity(): RememberedGoogleIdentity | null {
  return read<RememberedGoogleIdentity>(KEY_GOOGLE_IDENTITY);
}

export function setRememberedGoogleIdentity(identity: RememberedGoogleIdentity): void {
  write(KEY_GOOGLE_IDENTITY, identity);
}

export function clearRememberedGoogleIdentity(): void {
  localStorage.removeItem(KEY_GOOGLE_IDENTITY);
}

export type SyncEntityKey =
  | `warband:${string}`
  | `portrait:${string}:${string}`
  | `custom-creature:${string}`
  | `custom-weapon:${string}`
  | `custom-armour-item:${string}`
  | `custom-buff:${string}`
  | 'custom-bestiary'
  | 'custom-weapons'
  | 'custom-armour'
  | 'custom-buffs';

export interface SyncOutboxEntry {
  entityKey: SyncEntityKey;
  queuedAt: string;
  baseRevision: number | null;
}

export interface SyncEntityMetadata {
  revision: number;
  contentHash: string;
  modifiedAt: string;
  deviceId: string;
  deleted?: boolean;
}

export function getOrCreateSyncDeviceId(): string {
  const existing = localStorage.getItem(KEY_DEVICE_ID);
  if (existing) return existing;
  const deviceId = globalThis.crypto?.randomUUID?.()
    ?? `device-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(KEY_DEVICE_ID, deviceId);
  return deviceId;
}

export function getSyncOutbox(accountId: string): SyncOutboxEntry[] {
  return read<SyncOutboxEntry[]>(syncOutboxKey(accountId)) ?? [];
}

export function enqueueSyncEntity(
  accountId: string,
  entityKey: SyncEntityKey,
  baseRevision: number | null = null
): void {
  const outbox = getSyncOutbox(accountId);
  const existing = outbox.find((entry) => entry.entityKey === entityKey);
  const next: SyncOutboxEntry = {
    entityKey,
    queuedAt: new Date().toISOString(),
    baseRevision: existing?.baseRevision ?? baseRevision,
  };
  write(
    syncOutboxKey(accountId),
    existing
      ? outbox.map((entry) => (entry.entityKey === entityKey ? next : entry))
      : [...outbox, next]
  );
}

export function removeSyncEntity(accountId: string, entityKey: SyncEntityKey): void {
  write(
    syncOutboxKey(accountId),
    getSyncOutbox(accountId).filter((entry) => entry.entityKey !== entityKey)
  );
}

export function claimUnassignedSyncEntities(accountId: string): void {
  const unassignedAccount = '__unassigned__';
  for (const entry of getSyncOutbox(unassignedAccount)) {
    enqueueSyncEntity(accountId, entry.entityKey, null);
  }
  write(syncOutboxKey(unassignedAccount), []);
}

export function getSyncMetadata(accountId: string): Record<string, SyncEntityMetadata> {
  return read<Record<string, SyncEntityMetadata>>(syncMetadataKey(accountId)) ?? {};
}

export function setSyncEntityMetadata(
  accountId: string,
  entityKey: SyncEntityKey,
  metadata: SyncEntityMetadata
): void {
  write(syncMetadataKey(accountId), {
    ...getSyncMetadata(accountId),
    [entityKey]: metadata,
  });
}

/**
 * Resets uses for all refreshAfterGame items back to full (call at end of each game).
 * Items with refreshAfterGame=false are left unchanged (they are consumed permanently).
 */
export function resetRefreshableItemUses(warbandId: string): void {
  const data = loadWarbandData(warbandId);
  if (!data?.itemUses) return;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
  const treasureItems = useReferenceDataStore.getState().treasureItems;
  const refreshableIds = new Set(
    treasureItems.filter((t) => t.refreshAfterGame).map((t) => t.id)
  );
  const updated: Record<string, number> = {};
  for (const [key, value] of Object.entries(data.itemUses)) {
    const itemId = key.split(':').slice(1).join(':');
    if (!refreshableIds.has(itemId)) {
      updated[key] = value; // keep non-refreshable exhausted items
    }
    // refreshable items are simply omitted → getItemRemainingUses falls back to usesPerGame
  }
  data.itemUses = updated;
  saveWarbandData(warbandId, data);
}

// --------------------------------------------------------------------------
// Drive image ID helpers
// --------------------------------------------------------------------------

export function getMemberDriveImageId(warbandId: string, memberId: string): string | null {
  const data = loadWarbandData(warbandId);
  if (!data) return null;
  const member =
    data.wizard?.id === memberId
      ? data.wizard
      : data.apprentice?.id === memberId
        ? data.apprentice
        : data.captain?.id === memberId
          ? data.captain
          : data.soldiers.find((s) => s.id === memberId);
  if (member?.driveImageId) return member.driveImageId;
  const stargraveMember = data.stargrave?.captain?.id === memberId
    ? data.stargrave.captain
    : data.stargrave?.firstMate?.id === memberId
      ? data.stargrave.firstMate
      : data.stargrave?.soldiers.find((soldier) => soldier.id === memberId);
  return stargraveMember?.driveImageId ?? null;
}

export function updateMemberDriveImageId(
  warbandId: string,
  memberId: string,
  driveImageId: string | null
): void {
  const data = loadWarbandData(warbandId);
  if (!data) return;
  if (data.wizard?.id === memberId) {
    setDriveImageId(data.wizard, driveImageId);
  } else if (data.apprentice?.id === memberId) {
    setDriveImageId(data.apprentice, driveImageId);
  } else if (data.captain?.id === memberId) {
    setDriveImageId(data.captain, driveImageId);
  } else {
    const soldier = data.soldiers.find((s) => s.id === memberId);
    if (soldier) setDriveImageId(soldier, driveImageId);
    else if (data.stargrave?.captain?.id === memberId) data.stargrave.captain.driveImageId = driveImageId ?? undefined;
    else if (data.stargrave?.firstMate?.id === memberId) data.stargrave.firstMate.driveImageId = driveImageId ?? undefined;
    else {
      const stargraveSoldier = data.stargrave?.soldiers.find((candidate) => candidate.id === memberId);
      if (stargraveSoldier) stargraveSoldier.driveImageId = driveImageId ?? undefined;
    }
  }
  saveWarbandData(warbandId, data);
}

function setDriveImageId(member: StoredMember, driveImageId: string | null): void {
  if (driveImageId) {
    member.driveImageId = driveImageId;
  } else {
    delete member.driveImageId;
  }
}

export function getPendingMemberImage(
  warbandId: string,
  memberId: string
): PendingMemberImage | null {
  return read<PendingMemberImage>(pendingMemberImageKey(warbandId, memberId));
}

export function savePendingMemberImage(pending: PendingMemberImage): void {
  write(pendingMemberImageKey(pending.warbandId, pending.memberId), pending);
}

export function deletePendingMemberImage(warbandId: string, memberId: string): void {
  localStorage.removeItem(pendingMemberImageKey(warbandId, memberId));
}

export function getPendingMemberImages(warbandId?: string): PendingMemberImage[] {
  const pending: PendingMemberImage[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(PENDING_MEMBER_IMAGE_PREFIX)) continue;
    const value = read<PendingMemberImage>(key);
    if (value && (!warbandId || value.warbandId === warbandId)) pending.push(value);
  }
  return pending;
}

export function deletePendingMemberImages(warbandId: string): void {
  for (const pending of getPendingMemberImages(warbandId)) {
    deletePendingMemberImage(pending.warbandId, pending.memberId);
  }
}
